import React, { useEffect, useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Trash, ShieldWarning, Bell } from "@phosphor-icons/react";

export default function Settings() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [blocks, setBlocks] = useState([]);
  const [notifEnabled, setNotifEnabled] = useState(false);

  useEffect(() => {
    api.get("/users/blocks").then((r) => setBlocks(r.data));
    setNotifEnabled(typeof Notification !== "undefined" && Notification.permission === "granted");
  }, []);

  const enableNotifications = async () => {
    if (typeof Notification === "undefined") {
      toast.error("Notifications not supported on this device");
      return;
    }
    const perm = await Notification.requestPermission();
    if (perm === "granted") {
      // Stub: register token for FCM later. We register the device identity now.
      const stubToken = `web_${Math.random().toString(36).slice(2)}`;
      await api.post("/notifications/token", { token: stubToken, platform: "web" });
      setNotifEnabled(true);
      toast.success("Notifications enabled");
    } else {
      toast.error("Notifications blocked");
    }
  };

  const deleteAccount = async () => {
    if (!window.confirm("Delete your account? You can recover within 30 days by signing back in.")) return;
    try {
      await api.delete("/users/me");
      toast.success("Account deletion requested");
      logout();
    } catch (e) {
      toast.error("Could not delete account");
    }
  };

  const unblock = async (id) => {
    await api.delete(`/users/block/${id}`);
    setBlocks((b) => b.filter((x) => x.blocked_user_id !== id));
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <div className="pt-24 pb-16 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">Account</div>
        <h1 className="font-display text-4xl mb-8">Settings</h1>

        <section className="border border-slate-200 p-6 mb-6">
          <h2 className="font-display text-xl mb-3">Profile</h2>
          <div className="text-sm text-slate-700">{user?.name}</div>
          <div className="text-sm text-slate-500">{user?.email}</div>
          <div className="text-xs text-slate-500 mt-1 uppercase tracking-wider">Role: {user?.role || "—"}</div>
        </section>

        <section className="border border-slate-200 p-6 mb-6">
          <h2 className="font-display text-xl mb-1 flex items-center gap-2"><Bell size={20} /> Push notifications</h2>
          <p className="text-sm text-slate-600 mb-3">Get notified about new leads, messages, and reviews.</p>
          <button onClick={enableNotifications} className="bg-slate-900 text-white px-4 py-2 text-sm font-semibold" data-testid="enable-notifications-btn">
            {notifEnabled ? "Enabled · Re-sync" : "Enable notifications"}
          </button>
          <p className="text-xs text-slate-400 mt-2">FCM-ready. Server-side broadcast wired when Firebase keys are added.</p>
        </section>

        <section className="border border-slate-200 p-6 mb-6">
          <h2 className="font-display text-xl mb-3">Blocked users</h2>
          {blocks.length === 0 ? (
            <p className="text-sm text-slate-500">You haven&apos;t blocked anyone.</p>
          ) : (
            <ul className="space-y-2">
              {blocks.map((b) => (
                <li key={b.blocked_user_id} className="flex items-center justify-between text-sm">
                  <span className="font-mono text-xs">{b.blocked_user_id}</span>
                  <button onClick={() => unblock(b.blocked_user_id)} className="text-brand font-semibold" data-testid={`unblock-${b.blocked_user_id}`}>Unblock</button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="border-2 border-accent-red p-6">
          <h2 className="font-display text-xl mb-1 flex items-center gap-2 text-accent-red"><ShieldWarning size={20} /> Danger zone</h2>
          <p className="text-sm text-slate-600 mb-3">Delete your account. Sign back in within 30 days to recover.</p>
          <button onClick={deleteAccount} className="bg-accent-red text-white px-4 py-2 text-sm font-semibold flex items-center gap-1" data-testid="delete-account-btn">
            <Trash size={14} /> Delete account
          </button>
        </section>
      </div>
      <Footer />
    </div>
  );
}
